/**
 * Naive ASCII scan over the raw PDF binary to recover any embedded
 * plain text segments.
 *
 * This is NOT a real PDF parser.
 * It only works for PDFs that contain directly embedded selectable text.
 *
 * It will fail for:
 * - Image-based PDFs (scanned documents)
 * - Compressed/encoded text streams
 * - Most modern PDFs with object streams
 *
 * This exists only as a temporary fallback until a proper server-side
 * PDF parsing or OCR solution is implemented.
 */
export async function extractText(file: File): Promise<string> {
  try {
    // Temporary client-side fallback. Proper PDF parsing must be done server-side.

    if (!file.type.includes("pdf")) {
      throw new Error("Arquivo deve ser um PDF");
    }

    // Raw binary read (heuristic attempt to recover printable characters)
    const buffer = await file.arrayBuffer();
    const view = new Uint8Array(buffer);

    // Scan for printable ASCII ranges inside the PDF byte stream
    let text = "";
    let inText = false;

    for (const charCode of view) {
      if (charCode >= 32 && charCode <= 126) {
        text += String.fromCharCode(charCode);
        inText = true;
      } else if (charCode === 10 || charCode === 13) {
        if (inText) {
          text += "\n";
          inText = false;
        }
      }
    }

    // Basic sanitation of non-printable characters
    text = text
      .replace(/[^\w\s\.\,\!\?\-\(\)\"\'\n]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    if (!text || text.length < 10) {
      throw new Error(
        "Não foi possível extrair texto do PDF. Tente um PDF com texto selecionável. Para PDFs com apenas imagens, use OCR.",
      );
    }

    console.log(`[PDF] ✓ Extração: ${text.length} caracteres`);
    return text;
  } catch (error) {
    console.error("[PDF] ✗ Erro:", error);
    throw error;
  }
}

/**
 * Validates basic PDF file constraints before processing.
 * Does not guarantee the file content is a valid or readable PDF.
 */

export function validatePDFFile(file: File): boolean {
  if (!file) {
    throw new Error("Nenhum arquivo foi fornecido");
  }

  const isPDF =
    file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
  const maxSize = 50 * 1024 * 1024;

  if (!isPDF) {
    throw new Error("Por favor, selecione um arquivo PDF válido");
  }

  if (file.size > maxSize) {
    throw new Error("O arquivo PDF deve ter no máximo 50MB");
  }

  if (file.size === 0) {
    throw new Error("O arquivo está vazio");
  }

  return true;
}
